'use client';
import React, { useRef } from 'react';

/**
 * Hook para debuggar re-renders de componentes memorizados
 * Útil para identificar quais props estão causando re-renders desnecessários
 */
export const useWhyDidYouUpdate = (
  name: string,
  props: Record<string, any>
) => {
  const isDebugEnabled = process.env.NEXT_PUBLIC_ENABLE_TABLE_DEBUG === 'true';

  // Ref para guardar as props anteriores
  const previousProps = useRef<Record<string, any>>({});

  React.useEffect(() => {
    if (previousProps.current && isDebugEnabled) {
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      const changedProps: Record<string, any> = {};

      allKeys.forEach((key) => {
        if (previousProps.current![key] !== props[key]) {
          changedProps[key] = {
            from: previousProps.current![key],
            to: props[key],
          };
        }
      });

      if (Object.keys(changedProps).length) {
        console.log('[why-did-you-update]', name, changedProps);
      }
    }

    previousProps.current = props;
  });
};

/**
 * Função de comparação melhorada para React.memo
 * Compara as propriedades importantes da tabela de forma detalhada
 */
export const tablePropsAreEqual = (prevProps: any, nextProps: any): boolean => {
  const isDebugEnabled = process.env.NEXT_PUBLIC_ENABLE_TABLE_DEBUG === 'true';

  // Lista de propriedades críticas que devem causar re-render quando mudarem
  const criticalProps = [
    'data',
    'loading',
    'columns',
    'title',
    'page',
    'pageSize',
    'countPages',
    'searchValue',
    'enableSearch',
    'tableConfigName',
    'backgroundColor',
    'textColor',
    'withCard',
    'includeRowSelection',
    'variant',
    'emptyPlaceholder',
    'tableFilters',
    'cardFooter',
  ];

  // Lista de propriedades de função que devem ser comparadas por referência
  const functionProps = [
    'onPageChange',
    'onPageSizeChange',
    'handleSearch',
    'onDeleteSelected',
    'onSelectItems',
    'onTableStateChange',
  ];

  // Verifica props críticas
  for (const prop of criticalProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      if (isDebugEnabled) {
        console.log(`[Table Memo] Re-render causado por mudança em: ${prop}`);
      }
      return false;
    }
  }

  // Verifica props de função
  for (const prop of functionProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      if (isDebugEnabled) {
        console.log(
          `[Table Memo] Re-render causado por mudança na função: ${prop}`
        );
      }
      return false;
    }
  }

  // Compara arrays especiais (actions, tableActions)
  if (!arraysAreEqual(prevProps.actions, nextProps.actions)) {
    if (isDebugEnabled) {
      console.log('[Table Memo] Re-render causado por mudança em: actions');
    }
    return false;
  }

  if (!arraysAreEqual(prevProps.tableActions, nextProps.tableActions)) {
    if (isDebugEnabled) {
      console.log(
        '[Table Memo] Re-render causado por mudança em: tableActions'
      );
    }
    return false;
  }

  // Se chegou até aqui, as props são iguais
  if (isDebugEnabled) {
    console.log('[Table Memo] Props são iguais, evitando re-render');
  }
  return true;
};

/**
 * Compara dois arrays de forma superficial
 */
const arraysAreEqual = (arr1: any[], arr2: any[]): boolean => {
  if (!arr1 && !arr2) return true;
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;

  return arr1.every((item, index) => item === arr2[index]);
};
